import { derechosHeroData } from '@/mocks/derechos-data';

interface DerechosHeroProps {
  onScrollTo: (slug: string) => void;
}

export default function DerechosHero({ onScrollTo }: DerechosHeroProps) {
  return (
    <section className="relative w-full min-h-[90vh] flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Candid%20documentary%20photograph%20of%20musicians%20deeply%20immersed%20in%20a%20late%20night%20recording%20session%2C%20warm%20red%20and%20amber%20studio%20lighting%20casting%20dramatic%20shadows%2C%20multiple%20artists%20collaborating%20around%20analog%20equipment%2C%20genuine%20creative%20atmosphere%2C%20no%20posing%20or%20eye%20contact%20with%20camera%2C%20cinematic%20composition%20with%20rich%20textures%20and%20organic%20grain%2C%20editorial%20music%20documentary%20style%2C%20powerful%20emotional%20depth&width=1920&height=1080&seq=soniem-dv3-hero-bg&orientation=landscape"
          alt="Artistas musicales en sesión de grabación"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85" />
        <div className="absolute inset-0 bg-primary-900/15 mix-blend-multiply" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-primary-500" />
              <span className="text-white/90 text-xs md:text-sm font-medium tracking-wide uppercase">
                Sección Institucional
              </span>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white text-center leading-tight mb-4 md:mb-6 animate-fade-in-delayed">
            {derechosHeroData.titulo}
          </h1>

          {/* Subtitle */}
          <p className="text-white/65 text-sm md:text-base lg:text-lg text-center max-w-3xl mx-auto leading-relaxed mb-12 md:mb-16 animate-fade-in-delayed-2">
            {derechosHeroData.subtitulo}
          </p>

          {/* Two Module Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-8 animate-fade-in-delayed-3">
            {derechosHeroData.modulos.map((modulo) => (
              <div
                key={modulo.slug}
                className="group relative bg-white/8 backdrop-blur-sm border border-white/15 rounded-3xl overflow-hidden transition-all duration-500 hover:bg-white/12 hover:border-white/25 cursor-pointer"
                onClick={() => onScrollTo(modulo.slug)}
              >
                {/* Image */}
                <div className="relative h-48 md:h-56 overflow-hidden">
                  <img
                    src={modulo.imagen}
                    alt={modulo.titulo}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  {/* Icon badge */}
                  <div className="absolute top-4 left-4 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <i className={`${modulo.slug === 'fonogramas' ? 'ri-disc-line' : 'ri-film-line'} text-white text-lg md:text-xl`} />
                  </div>
                </div>

                {/* Text */}
                <div className="p-5 md:p-7">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">
                    {modulo.titulo}
                  </h3>
                  <p className="text-white/50 text-xs font-medium tracking-wide uppercase mb-3">
                    {modulo.subtitulo}
                  </p>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    {modulo.descripcion}
                  </p>
                  <button
                    className="inline-flex items-center gap-2 text-primary-400 text-sm font-semibold group-hover:gap-3 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <span>Conocer más</span>
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom brand text */}
      <div className="absolute bottom-0 left-0 right-0 pb-4 md:pb-8">
        <div className="px-4 md:px-6 lg:px-10">
          <p className="font-heading text-white/[0.06] text-[10vw] md:text-8xl lg:text-9xl font-black leading-none select-none tracking-tighter">
            DERECHOS
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-3 md:bottom-5 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/40 text-xs font-medium tracking-widest uppercase">Explorar</span>
          <div className="w-5 h-8 rounded-full border-2 border-white/25 flex items-start justify-center p-1">
            <div className="w-1 h-2 rounded-full bg-white/50 animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
}